import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import type { BounceCategory } from '@/lib/types'

const HARD_BOUNCE_CATEGORIES = new Set([
  'InvalidMailbox',
  'PolicyRejection',
  'InfrastructureBlock',
  'ContentRejection',
])

const HARD_COLOR = '#ef4444'
const SOFT_COLOR = '#f59e0b'

interface BounceCategoryChartProps {
  categories: BounceCategory[]
}

export function BounceCategoryChart({ categories }: BounceCategoryChartProps): React.JSX.Element {
  const chartData = categories.map((cat) => ({
    name: cat.category.replace(/([A-Z])/g, ' $1').trim(),
    count: cat.count,
    isHard: HARD_BOUNCE_CATEGORIES.has(cat.category),
  }))

  return (
    <div className="rounded-xl border border-surface-3 bg-surface-0 p-6">
      <div className="mb-4">
        <h2 className="text-sm font-medium text-text-primary">Bounce Breakdown by Category</h2>
        <p className="text-xs text-text-tertiary">
          <span className="inline-block h-2 w-2 rounded-full bg-red-500" /> Hard bounces{' '}
          <span className="ml-2 inline-block h-2 w-2 rounded-full bg-amber-500" /> Soft bounces
        </p>
      </div>
      {categories.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <p className="text-sm text-text-secondary">No bounce data for this period</p>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData} layout="vertical" margin={{ left: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-surface-3)" />
            <XAxis type="number" stroke="var(--color-text-tertiary)" fontSize={11} />
            <YAxis
              type="category"
              dataKey="name"
              stroke="var(--color-text-tertiary)"
              fontSize={11}
              width={140}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'var(--color-surface-0)',
                border: '1px solid var(--color-surface-3)',
                borderRadius: '8px',
                fontSize: '12px',
              }}
            />
            <Bar dataKey="count" name="Bounces" radius={[0, 4, 4, 0]}>
              {chartData.map((entry) => (
                <Cell key={entry.name} fill={entry.isHard ? HARD_COLOR : SOFT_COLOR} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  )
}
